import {
  ChangeEventArgs,
  ToolbarSettingsModel,
} from "@syncfusion/ej2-dropdowns";
import {
  RichTextEditorFieldProps,
  RichTextEditorValueType,
} from "./RichTextEditorField";
import { useEffect, useRef, useState } from "react";
import {
  RichTextEditorComponent,
  RichTextEditorModel,
} from "@syncfusion/ej2-react-richtexteditor";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base/src";
import { RichTextEditorFieldValue } from "../../types/fieldTypes";

/**Hooks for Rich Text Editor Field*/
export const useRichTextEditorField = (props: RichTextEditorFieldProps) => {
  const richTextEditorRef = useRef<RichTextEditorComponent | null>(null);
  const [ready, setReady] = useState<boolean>(false);

  const [richTextEditorSettings, setRichTextEditorSettings] =
    useState<RichTextEditorModel>();

  const encodeToBase64 = (data: string) => {
    return window.btoa(unescape(encodeURIComponent(data))); // btoa(data);
  };

  const decodeFromBase64 = (data: string) => {
    return decodeURIComponent(escape(window.atob(data))); //atob(data);
  };

  const getPropValue = (propValue?: string): string | undefined => {
    let val = propValue;
    if (val && props.valueType === RichTextEditorValueType.base64) {
      val = decodeFromBase64(val);
    }
    return val;
  };
  const [localValue, setLocalValue] = useState<any>(getPropValue(props.value));

  useEffect(() => {
    setup();
  }, []);

  useEffect(() => {
    if (richTextEditorRef.current) {
      let val = localValue;
      if (val && props.valueType === RichTextEditorValueType.base64) {
        val = encodeToBase64(val);
      }

      const plainText =
        richTextEditorRef.current?.getText &&
        richTextEditorRef.current.getText();

      props.getPlainTextOnSubmit && props.getPlainTextOnSubmit(plainText);
      props.onSubmit && props.onSubmit(val);
    }
  }, [localValue]);

  useEffect(() => {
    if (richTextEditorRef.current) {
      handleValueChange(getPropValue(props.value));
    }
  }, [props.value]);

  const setup = async () => {
    // Setup Properties
    let settings = {
      ...defaultRichTextEditorSettings,
      ...(props.richTextEditorSettings || {}),
    } as RichTextEditorModel;

    if (props.readOnly && settings.toolbarSettings) {
      settings.toolbarSettings.enable = false;
    }

    if (
      (settings as RichTextEditorComponent).toolbarSettings?.items &&
      !props.showFullScreenOption
    ) {
      (settings as RichTextEditorComponent).toolbarSettings.items = (
        settings as RichTextEditorComponent
      ).toolbarSettings?.items?.filter((item) => item !== "FullScreen");
    }

    setRichTextEditorSettings(settings);

    setReady(true);
  };

  const handleValueChange = (newValue: RichTextEditorFieldValue) => {
    if (richTextEditorRef.current) {
      setLocalValue(newValue);
      richTextEditorRef.current.value = newValue || "";
    }
  };

  const defaultRichTextEditorSettings: RichTextEditorModel &
    DefaultHtmlAttributes = {
    toolbarSettings: {
      items: [
        "Bold",
        "Italic",
        "Underline",
        "StrikeThrough",
        "|",
        "Formats",
        "Alignments",
        "OrderedList",
        "UnorderedList",
        "Outdent",
        "Indent",
        "|",
        "ClearFormat",
        "FullScreen",
        "CreateLink",
      ],
    } as ToolbarSettingsModel,
    iframeSettings: { enable: true },
    change: (args: ChangeEventArgs) => {
      handleValueChange(args.value as string);
    },
    enterKey: "P",
    pasteCleanupSettings: {
      prompt: false,
      plainText: false,
      keepFormat: true,
      allowedStyleProps: [""],
      deniedAttrs: ["class", "id", "style"],
      deniedTags: ["a"],
    },
  };

  return { richTextEditorRef, localValue, richTextEditorSettings, ready };
};
